// Considered: TooltipAtom (PrimeReact text tooltip), OverlayPanelAtom — rejected because Tooltip
// is text-only and cannot host a multi-line dark card, and OverlayPanel is click-anchored (not
// hover-safe, which would violate "hover never commits"). See spec Reuse Audit
// (specs/stream-trajectory-tools.md).
//
// Rich hover peek, portaled to `body` so ancestor transforms (the metaspace artboard) can't clip
// it. NEVER commits — it has no click handler and never calls an inspect/act callback. Source:
// design/metaspace-tools/metaspace-interactions.jsx `MSPeek`.

import React, { useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './PeekAtom.scss';

export interface PeekAtomProps {
  /** Content rendered inside the floating card. Omit to render children with no peek behavior. */
  peek?: React.ReactNode;
  children: React.ReactNode;
  /** Renders the trigger as a block element instead of inline-flex. */
  block?: boolean;
  /** Flex-grow factor applied to the trigger when it sits in a flex row. */
  grow?: number;
  className?: string;
}

interface PeekPosition {
  x: number;
  y: number;
}

const PeekAtom: React.FC<PeekAtomProps> = ({ peek, children, block, grow, className }) => {
  const [pos, setPos] = useState<PeekPosition | null>(null);
  const descriptionId = useId();
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

  const show = (target: HTMLElement) => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    const rect = target.getBoundingClientRect();
    setPos({ x: rect.left + rect.width / 2, y: rect.top });
  };

  const hide = () => {
    setPos(null);
  };

  const triggerClasses = [
    'peek-atom__trigger',
    block ? 'peek-atom__trigger--block' : '',
    pos ? 'peek-atom__trigger--active' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <span
        className={triggerClasses}
        style={grow ? { flex: `${grow} 1 0%`, minWidth: 0 } : undefined}
        onMouseEnter={(e) => show(e.currentTarget)}
        onMouseLeave={hide}
        onFocus={(e) => show(e.currentTarget)}
        onBlur={hide}
        aria-describedby={peek ? descriptionId : undefined}
      >
        {children}
      </span>
      {pos && peek
        ? ReactDOM.createPortal(
            <div
              id={descriptionId}
              role="tooltip"
              className="peek-atom__card"
              style={{ left: pos.x, top: pos.y - 9 }}
            >
              {peek}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

PeekAtom.displayName = 'PeekAtom';

export default PeekAtom;
